import { memo, ReactElement } from 'react'

import isEqual from 'lodash/isEqual'

import { updateKeyword } from 'api/Cerebro/orgScoped/amazonAdsApi'
import { useResource } from 'components/EditableFields/localHooks'
import { ARCHIVED } from 'const/resourceStates'
import { userHasManageAmsPermissions } from 'helpers/featurePermissions'
import { usePermissionMessage, useUserHasPermissions } from 'hooks'
import { KeywordGroupBy } from 'types'

import { DeleteField } from '../../SharedFields/DeleteField'

interface Props<RecordType> {
    dataIndex: string[]
    readOnly: boolean
    record: RecordType
    reloadData: () => void
}

function ArchiveKeywordField<RecordType extends KeywordGroupBy>({
    dataIndex = ['keyword'],
    readOnly = false,
    record,
    reloadData,
}: Props<RecordType>): ReactElement {
    const hasPermission = useUserHasPermissions(userHasManageAmsPermissions)
    const permissionMessage = usePermissionMessage()
    const { getFromResource, resourceId } = useResource<RecordType>(
        record,
        dataIndex,
        'keyword'
    )

    const userCanManageKeyword = getFromResource(['user_can_manage'], true)

    const getTooltipTitle = (): string | undefined => {
        if (userCanManageKeyword) {
            return undefined
        }

        return hasPermission
            ? (getFromResource(['user_can_manage_detail']) ?? permissionMessage)
            : permissionMessage
    }

    return (
        <DeleteField
            disabled={!userCanManageKeyword || readOnly}
            okText="Archive"
            title="Are you sure you want to archive this Keyword?"
            tooltipTitle={getTooltipTitle()}
            updateRequestApi={updateKeyword}
            updateRequestCallback={reloadData}
            updateRequestData={resourceId}
            updateRequestParams={{ state: ARCHIVED }}
            updateRequestSuccessMesg="Keyword successfully archived"
        />
    )
}

export default memo(ArchiveKeywordField, isEqual) as typeof ArchiveKeywordField
